import { useEffect, useMemo, useState } from "react";
import { get } from "lodash";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UploadIcon } from "@heroicons/react/outline";
import { clubApi } from "routes/club/api/club.service";
import { generateUUID } from "utills/helpers";
import { Modal } from "components/modal";
import Button from "components/button";
import {
  ClubMassType,
  ClubType,
  ClubPayload,
  ClubMassPayload,
} from "routes/club/types";
import { FailedRowType } from "routes/club/hooks/useCreateMass";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import CheckBox from "../CheckBox";
import Input from "components/input";
import Pagination from "components/pagination";
import { FullScreenLoading } from "components/fullScreenLoading";

export const formEmptyValue = {
  is_active: true,
  create_mass: false,
  label_vi: "",
  label_en: "",
};

export interface InValidTableType {
  index: string | number;
  description: string[];
}

interface PagingType {
  total_item: number;
  total_page: number;
  current_page: number;
}

interface PropType {
  open: boolean;
  loading: boolean;
  targetClub: ClubType | null;
  handleClose: () => void;
  handleSubmitModalForm: (
    value: ClubPayload | ClubMassPayload[],
    mode: "create" | "create-mass" | "edit",
    resetFormApi: any, // React hook form reset method
    handleFormatMassImportFailed: (data: FailedRowType[]) => void,
    handleMassReset: () => void // Mass data reset method
  ) => Promise<void>;
}

const FormModal = (props: PropType) => {
  const { open, loading, targetClub, handleClose, handleSubmitModalForm } =
    props;

  const ALLOWED_TYPES = [
    "text/csv", // CSV
    "application/vnd.ms-excel", // XLS (cũ)
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
  ];

  const HEADER_TEMPLATE = [
    { value: "club_name_vie", label: "Club name VIE" },
    { value: "club_name_eng", label: "Club name ENG" },
    { value: "key", label: "Key" },
    // { value: "status", label: "Status" },
  ];

  const STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const PAGE_SIZE = 10;

  const VALID_STATUS = STATUS_OPTIONS.map((opt) => opt.value);
  const VALID_STATUS_LABEL = STATUS_OPTIONS.map((opt) => opt.label);

  const [filter, setFilter] = useState<{ page: number }>({ page: 1 });
  const [massData, setMassData] = useState<ClubMassType[]>([]);
  const [loadingImport, setLoadingImport] = useState<boolean>(false);
  const [isImportComplete, setIsImportComplete] = useState<boolean>(false);
  const [isErrorImport, setIsErrorImport] = useState<boolean>(false);
  const [inValidData, setInValidData] = useState<InValidTableType[]>([]);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);

  // Paginated invalid import data
  const PAGINATED_INVALID_TABLE_DATA = useMemo(() => {
    if (!inValidData) return [];

    const start = (filter.page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    return inValidData?.slice(start, end);
  }, [inValidData, filter.page]);

  // Invalid import paging data
  const invalidPaging: PagingType = useMemo(() => {
    return {
      total_item: inValidData?.length || 0,
      total_page: Math.ceil((inValidData?.length || 0) / PAGE_SIZE),
      current_page: filter.page,
    };
  }, [inValidData, filter.page]);

  const FormSchema = z
    .object({
      is_active: z.boolean(),
      create_mass: z.boolean(),
      label_vi: z.string(),
      label_en: z.string(),
    })
    .superRefine((data, ctx) => {
      if (!data.create_mass) {
        if (!data.label_vi || data.label_vi.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required",
            path: ["label_vi"],
          });
        }

        if (!data.label_en || data.label_en.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required",
            path: ["label_en"],
          });
        }
      }
    });

  const ClubImportFileSchema = z.array(
    z.object({
      club_name_vie: z
        .string({
          required_error: "Club name VIE is required",
        })
        .min(1, { message: "Club name VIE is required" }),
      club_name_eng: z
        .string({
          required_error: "Club name ENG required",
        })
        .min(1, { message: "Club name ENG required" }),
      key: z
        .string({
          required_error: "Key is required",
        })
        .min(1, { message: "Key is required" }),
      // status: z
      //   .string()
      //   .refine((val) => VALID_STATUS.includes(val.toLowerCase()), {
      //     message: `Invalid status. Includes values: ${VALID_STATUS_LABEL.join(
      //       ", "
      //     )}`,
      //   }),
    })
  );

  type FormData = z.infer<typeof FormSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setFocus,
    setValue,
    watch,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: formEmptyValue,
  });

  const is_create_mass = watch("create_mass");

  // Update init value when edit
  useEffect(() => {
    const handleGetDetailClub = async () => {
      try {
        setDetailLoading(true);
        const res = await clubApi.detail(targetClub?.id ?? "");

        if (res?.data?.result && res?.data?.data) {
          const initValue: ClubType = res?.data?.data;
          console.log("DETAIL CLUB:", initValue);

          // Update form vallue
          reset(
            initValue ? { ...initValue, create_mass: false } : formEmptyValue
          );
        }
      } catch (error) {
        console.log("Get detail club failed:", error);
      } finally {
        setDetailLoading(false);
      }
    };

    if (targetClub) handleGetDetailClub();
    else reset(formEmptyValue);
  }, [targetClub]);

  const handleReset = () => {
    setMassData([]);
    setIsImportComplete(false);
    setIsErrorImport(false);
    setInValidData([]);
    setFilter({
      page: 1,
    });
  };

  const handleCloseModal = () => {
    handleReset();
    reset(formEmptyValue);
    handleClose();
  };

  const handleChooseFile = async () => {
    const input = document.createElement("input");
    input.type = "file";

    input.onchange = async (e: any) => {
      handleReset();

      const file = e.target.files[0];
      const reader = new FileReader();

      if (!file) {
        toast.error("Can not read choosen file");
        return;
      }

      // Kiểm tra loại file
      if (file && !ALLOWED_TYPES.includes(file.type)) {
        toast.error("File format valid: .csv, .xlsx");
        return;
      }

      setLoadingImport(true);

      reader.onload = (evt: any) => {
        try {
          const arrayBuffer = evt.target.result as ArrayBuffer;

          // Đọc file bằng arrayBuffer thay vì binary string
          const wb = XLSX.read(arrayBuffer, { type: "array" });

          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          const rawData = XLSX.utils.sheet_to_json(ws, {
            raw: false,
          }) as ClubType[];

          // Kiểm tra template format
          const header = XLSX.utils.sheet_to_json(ws, {
            header: 1, // lấy toàn bộ file dạng mảng 2D
            range: 0, // chỉ lấy dòng đầu tiên
          })[0] as string[];

          const isHeaderValid = header?.length === HEADER_TEMPLATE?.length;

          if (!isHeaderValid) {
            toast.error("Invalid file format, please view template file");
            setLoadingImport(false);
            return;
          }

          // Format dữ liệu
          const submitData: ClubMassType[] = [];

          if (rawData && rawData.length > 0) {
            rawData.forEach((item: ClubType) => {
              const obj: any = {
                club_name_vie: "",
                club_name_eng: "",
                key: "",
                // status: "",
              };

              Object.entries(obj).forEach(([key, value], index) => {
                const itemKey = HEADER_TEMPLATE[index].value;
                const itemLabel = HEADER_TEMPLATE[index].label; // tên cột trong Excel

                // Get item by import file label
                // const rawItemValue = item?.[itemLabel as keyof ClubType]
                //   ? (item?.[itemLabel as keyof ClubType] as string)
                //   : "";

                // Get item value by index
                const values = Object.values(item);
                const rawItemValue = values[index];

                // Format club status (status)
                // if (itemKey === "status") {
                //   obj[itemKey] = rawItemValue
                //     ? rawItemValue.toLowerCase()
                //     : STATUS_OPTIONS[0].value; // default is active value
                // } else {
                //   obj[itemKey] = rawItemValue;
                // }

                obj[itemKey] = rawItemValue;
              });

              submitData.push(obj);
            });
          }

          const validResult = ClubImportFileSchema.safeParse(submitData);

          console.log({ validResult, submitData });

          if (!validResult.success) {
            toast.error("Invalid data");
            setIsErrorImport(true);

            const errList: InValidTableType[] = [];

            validResult.error.issues.forEach((issue) => {
              const [index] = issue.path; // vị trí phần tử trong mảng
              if (typeof index !== "number") return; // bỏ qua lỗi không thuộc mảng

              const existing = errList.find((item) => item.index === index);

              if (existing) {
                existing.description.push(issue.message);
              } else {
                errList.push({
                  index,
                  description: [issue.message],
                });
              }
            });

            setInValidData(errList);
          } else {
            setMassData([...submitData]);
            setIsImportComplete(true);
            toast.success("Import successfully");
          }
        } finally {
          setLoadingImport(false);
        }
      };

      reader.readAsArrayBuffer(file);
    };

    input.click();
  };

  const handleFormatMassImportFailed = (data: FailedRowType[]) => {
    const formattedData = data.map((item) => ({
      index: item.row - 2,
      description: [
        `Name VI: ${item.data.club_name_vie}`,
        `Name ENG: ${item.data.club_name_eng}`,
        `Key: ${item.data.key}`,
        `Error: ${item.error}`,
      ],
    }));

    setIsErrorImport(true);
    setInValidData(formattedData);
  };

  const handlePageChange = (value: number) => {
    setFilter({ ...filter, page: value });
  };

  const onSubmit = async (data: FormData) => {
    if (is_create_mass) {
      console.log("Submit mass:", massData);

      const payload: ClubMassPayload[] = massData.map((mass) => {
        return {
          label_vi: mass.club_name_vie,
          label_en: mass.club_name_eng,
          key: mass.key,
        };
      });

      handleSubmitModalForm(
        payload,
        "create-mass",
        reset,
        handleFormatMassImportFailed,
        handleReset
      );
    } else {
      const mode = targetClub ? "edit" : "create";

      // Create mode
      if (mode === "create") {
        const payload: ClubPayload = {
          is_active: data.is_active,
          label_vi: data.label_vi,
          label_en: data.label_en,
        };

        handleSubmitModalForm(
          payload,
          "create",
          reset,
          handleFormatMassImportFailed,
          handleReset
        );
      }

      // Edit mode
      if (mode === "edit") {
        const payload: ClubPayload = {
          id: targetClub?.id,
          is_active: data.is_active,
          label_vi: data.label_vi,
          label_en: data.label_en,
        };

        handleSubmitModalForm(
          payload,
          "edit",
          reset,
          handleFormatMassImportFailed,
          handleReset
        );
      }
    }
  };

  const onError = (errors: any) => {
    console.error("Submit failed:", errors);

    const firstErrorKey = Object.keys(errors)[0];
    setFocus(firstErrorKey as any);
  };

  return (
    <>
      {detailLoading && <FullScreenLoading />}

      <Modal
        open={open}
        onClose={handleCloseModal}
        title={targetClub ? "Edit Club Info" : "Create Club"}
        closeOnClickBackdrop={false}
      >
        <form
          className="p-4 flex flex-col gap-5"
          onSubmit={handleSubmit(onSubmit, onError)}
        >
          <div className="w-full flex flex-wrap items-center justify-between">
            {/* Mass Create */}
            {targetClub ? (
              <div className="opacity-0">field</div>
            ) : (
              <CheckBox
                key={"mass-radio-button"}
                item={{
                  id: "mass-create",
                  title: "Create mass",
                }}
                disabled={targetClub ? true : false}
                checked={is_create_mass}
                onChange={() => {
                  setValue("create_mass", !is_create_mass);
                  handleReset();
                }}
              />
            )}

            {/* Status */}
            {is_create_mass ? null : (
              <Controller
                control={control}
                name="is_active"
                render={({ field, fieldState }) => (
                  <div className={`flex items-center gap-3`}>
                    <Label id="is_active" name="Status" className="mb-0" />

                    <SwitchButton
                      checked={field.value}
                      onChange={() => {
                        setValue("is_active", !field.value);
                      }}
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            )}
          </div>

          {is_create_mass ? (
            <div className="w-full flex flex-col gap-5">
              <Button
                type="button"
                className="flex-1 h-10 px-0 max-w-full justify-center bg-white text-brand-500 ring-1 ring-inset ring-magnolia-700 hover:bg-magnolia-400 active:bg-magnolia-500 focus:ring-bg-magnolia-500"
                onClick={() => {
                  handleChooseFile();
                }}
              >
                <UploadIcon className="size-4 mr-2" /> Import File
              </Button>
            </div>
          ) : (
            <>
              <Controller
                control={control}
                name="label_vi"
                render={({ field, fieldState }) => (
                  <div className={`flex flex-col gap-1`}>
                    <Label
                      required={false}
                      id="label_vi"
                      name="Club Name (VIE)"
                    />

                    <Input
                      {...field}
                      type="text"
                      error={fieldState.error}
                      placeholder="Club Name"
                      className="bg-white"
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                )}
              />

              <Controller
                control={control}
                name="label_en"
                render={({ field, fieldState }) => (
                  <div className={`flex flex-col gap-1`}>
                    <Label
                      required={false}
                      id="label_en"
                      name="Club Name (ENG)"
                    />

                    <Input
                      {...field}
                      type="text"
                      error={fieldState.error}
                      placeholder="Club Name"
                      className="bg-white"
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </>
          )}

          {!loadingImport && !isErrorImport && isImportComplete && (
            <div className="w-full flex items-center justify-center">
              <span className="font-semibold">
                Added: {get(massData, "length", 0)} Club records
              </span>
            </div>
          )}

          {/* InValid Import Table */}
          {!loadingImport && isErrorImport && (
            <div className="mb-3 flex flex-col gap-5">
              <h3 className="text-[1.05rem] text-center font-bold">
                Invalid Club Data List
              </h3>

              <div className="w-full max-h-[300px] overflow-y-auto">
                <table className="mb-6 w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
                      >
                        Row
                      </th>

                      <th scope="col" className="th">
                        Description
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    {PAGINATED_INVALID_TABLE_DATA?.map(
                      (item: InValidTableType, index: number) => {
                        return (
                          <tr key={generateUUID()}>
                            <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                              {Number(get(item, "index", 0)) + 2}
                            </td>

                            <td className="td overflow-x-auto max-w-40">
                              <ul>
                                {item?.description?.map((msg: string) => {
                                  return (
                                    <li
                                      key={generateUUID()}
                                      className="whitespace-pre-wrap max-w-[280px]"
                                    >
                                      {msg}
                                    </li>
                                  );
                                })}
                              </ul>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={get(invalidPaging, "current_page", 1)}
                totalPage={get(invalidPaging, "total_page", 0)}
                total={{
                  totalItems: get(PAGINATED_INVALID_TABLE_DATA, "length", 0),
                  total: get(invalidPaging, "total_item", 0),
                }}
                onChange={handlePageChange}
              />
            </div>
          )}

          <div className="w-full">
            <div className="flex items-center justify-end gap-x-3">
              <Button
                className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                onClick={handleClose}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                className="w-20"
                loading={loading || loadingImport}
              >
                {targetClub ? "Save" : "Create"}
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default FormModal;
